/**
 * ChatFab — Messenger-style floating chat.
 *
 * Desktop: shows a floating chat button bottom-right. Clicking it opens
 * a compact conversations drawer. Selecting a conversation opens a
 * dedicated 328×452 floating chat WINDOW anchored to the bottom-right;
 * additional windows stack to the LEFT (Facebook Messenger layout).
 * Each window can be minimized, closed, or expanded to the full page.
 *
 * Mobile / small screens (< sm): selecting a room from the drawer
 * navigates to /doserne/chat?room=<id> (fullscreen experience).
 *
 * The FAB and windows render on every Doserne page via DoserneShell.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  MessageCircle, X, Users, Plus, Minus, Maximize2, Edit3, Bell, BellOff, Search,
} from "lucide-react";
import { useDoserneAuth } from "./doserneAuth";
import { RoomView, NewRoomModal, isOnline, otherMember, timeAgo } from "./ChatRoomView";
import { useChatSound } from "./useChatSound";
import { useDosernePush } from "./useDosernePush";
import ChatSearchModal from "./ChatSearchModal";
import { useLang } from "./i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WINDOW_W = 328;
const WINDOW_H = 452;
const WINDOW_GAP = 12;
const FAB_RESERVED = 88; // FAB (~64) + margin
const MAX_WINDOWS_CAP = 6; // hard sanity cap

// How many 328px chat windows actually fit across the current viewport.
function maxWindowsForWidth(w) {
  if (typeof w !== "number" || w <= 0) return 1;
  const fit = Math.floor((w - FAB_RESERVED - 12) / (WINDOW_W + WINDOW_GAP));
  return Math.max(1, Math.min(MAX_WINDOWS_CAP, fit));
}

function useMaxWindows() {
  const [n, setN] = useState(() => maxWindowsForWidth(typeof window !== "undefined" ? window.innerWidth : 1280));
  useEffect(() => {
    const onR = () => setN(maxWindowsForWidth(window.innerWidth));
    window.addEventListener("resize", onR);
    return () => window.removeEventListener("resize", onR);
  }, []);
  return n;
}

function useDesktop() {
  const [d, setD] = useState(() => typeof window !== "undefined" && window.innerWidth >= 768);
  useEffect(() => {
    const onR = () => setD(window.innerWidth >= 768);
    window.addEventListener("resize", onR);
    return () => window.removeEventListener("resize", onR);
  }, []);
  return d;
}

// -----------------------------------------------------------------
// FLOATING CHAT WINDOW (desktop only)
// -----------------------------------------------------------------
function ChatWindow({
  room, index, myEmail, authHeader, colleagueLabels,
  minimized, onMinimize, onClose, onExpand, onMuteToggled,
}) {
  const { t } = useLang();
  const rightOffset = FAB_RESERVED + index * (WINDOW_W + WINDOW_GAP);

  // Minimized: compact pill in the stack.
  if (minimized) {
    const label = room.type === "dm"
      ? (colleagueLabels[otherMember(room, myEmail)] || otherMember(room, myEmail))
      : (room.name || (room.type === "project" ? t("chat.header.project") : t("chat.header.group")));
    const online = room.type === "dm" && isOnline(room.other_last_seen_at);
    return (
      <div
        className="fixed z-40 bottom-4"
        style={{ right: `${rightOffset}px` }}
        data-testid={`chat-window-min-${room.id}`}
      >
        <div className="flex items-center gap-1 bg-white rounded-full shadow-xl border border-slate-200 pl-1 pr-2 py-1">
          <button
            onClick={onMinimize}
            className="relative w-9 h-9 rounded-full bg-gradient-to-br from-[#0084ff] to-[#0064d1] flex items-center justify-center flex-shrink-0"
            style={{ color: "#fff" }}
            title={label}
          >
            {room.type === "group" || room.type === "project"
              ? <Users className="w-4 h-4" />
              : <span className="text-xs font-black">{(label || "?").slice(0, 1).toUpperCase()}</span>}
            {online && <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-emerald-500 border-2 border-white" />}
          </button>
          <button
            onClick={onClose}
            className="w-6 h-6 rounded-full hover:bg-slate-100 text-slate-400 hover:text-slate-700 flex items-center justify-center"
            aria-label={t("fab.close")}
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed z-40 bottom-4 bg-white rounded-t-xl rounded-b-xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden"
      style={{ right: `${rightOffset}px`, width: `${WINDOW_W}px`, height: `${WINDOW_H}px` }}
      data-testid={`chat-window-${room.id}`}
    >
      <RoomView
        room={room}
        myEmail={myEmail}
        authHeader={authHeader}
        colleagueLabels={colleagueLabels}
        compact
        onMuteToggled={onMuteToggled}
        headerRight={
          <div className="flex items-center gap-0.5 ml-1">
            <button
              onClick={onExpand}
              className="w-7 h-7 rounded-full hover:bg-white/20 flex items-center justify-center"
              style={{ color: "#fff" }}
              title={t("fab.open_full")}
              data-testid={`chat-window-expand-${room.id}`}
            >
              <Maximize2 className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={onMinimize}
              className="w-7 h-7 rounded-full hover:bg-white/20 flex items-center justify-center"
              style={{ color: "#fff" }}
              title={t("fab.minimize")}
              data-testid={`chat-window-min-btn-${room.id}`}
            >
              <Minus className="w-4 h-4" />
            </button>
            <button
              onClick={onClose}
              className="w-7 h-7 rounded-full hover:bg-white/20 flex items-center justify-center"
              style={{ color: "#fff" }}
              title={t("fab.close")}
              data-testid={`chat-window-close-${room.id}`}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        }
      />
    </div>
  );
}

// -----------------------------------------------------------------
// FAB + DRAWER + WINDOWS
// -----------------------------------------------------------------
export default function ChatFab() {
  const { user, authHeader } = useDoserneAuth();
  const { t } = useLang();
  const isDesktop = useDesktop();
  const { soundOn, toggleSound, play: playDing } = useChatSound();
  const push = useDosernePush(authHeader);
  const [open, setOpen] = useState(false);
  const [rooms, setRooms] = useState([]);
  const [colleagues, setColleagues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [drawerQuery, setDrawerQuery] = useState("");
  // openWindows: [{ id, room, minimized }]
  const [openWindows, setOpenWindows] = useState([]);
  // Mobile overlay: shows a chat room as an in-app overlay on top of
  // the current page WITHOUT routing away. `mode = "full"` covers the
  // whole viewport under the sticky 64px header, `mode = "sheet"` is a
  // 70dvh bottom-sheet used when the user taps a chat push
  // notification. Set from `openRoom` (mobile) and from the
  // `doserne:open-chat-sheet` window event bridged by the SW.
  const [mobileOverlay, setMobileOverlay] = useState(null); // { room, mode, minimized }
  const location = useLocation();
  const navigate = useNavigate();
  // Remember last-seen unread counts per room so we can detect a NEW
  // incoming message → play the ding once. First load is silent.
  const unreadRef = useRef({});
  const firstLoad = useRef(true);
  const openWindowsRef = useRef([]);
  const maxWindows = useMaxWindows();
  const maxWindowsRef = useRef(maxWindows);
  useEffect(() => { maxWindowsRef.current = maxWindows; }, [maxWindows]);

  // Muted rooms don't contribute to the FAB badge (Messenger behaviour).
  const totalUnread = rooms.reduce(
    (sum, r) => sum + (r.muted ? 0 : (r.unread || 0)),
    0
  );

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/doserne/chat/rooms`, authHeader());
      const nextRooms = data.rooms || [];
      // Detect new incoming messages → play ding + in-app toast once.
      if (!firstLoad.current) {
        const openRoomIds = new Set(
          openWindowsRef.current.filter((w) => !w.minimized).map((w) => w.id)
        );
        let dingRoom = null;
        for (const r of nextRooms) {
          const prev = unreadRef.current[r.id] || 0;
          const cur = r.unread || 0;
          if (r.muted) continue;
          if (cur <= prev) continue;
          // Skip rooms the user is actively viewing (maximized window / full page).
          if (openRoomIds.has(r.id)) continue;
          if (dingRoom === null) dingRoom = r;

          const fresh = nextRooms.find((x) => x.id === r.id) || r;

          if (isDesktop) {
            // Desktop: AUTO-OPEN the chat window when a new message arrives
            // (Messenger-style). Newest opens on the right; older windows
            // stack left, capped to however many fit on screen.
            setOpenWindows((curW) => {
              const existing = curW.find((w) => w.id === r.id);
              if (existing) {
                return [{ ...existing, room: fresh, minimized: false }, ...curW.filter((w) => w.id !== r.id)];
              }
              return [{ id: r.id, room: fresh, minimized: false }, ...curW].slice(0, maxWindowsRef.current);
            });
            continue; // no toast needed — the window is now open
          }

          // Mobile: keep the tap-to-open toast (no floating windows on mobile).
          const lm = r.last_message || {};
          const from = lm.sender_name || (r.type === "dm" ? otherMember(r, user.email) : (r.name || "Doserne"));
          const bodyPrev = lm.audio_url ? `🎙️ ${t("chat.composer.voice_note")}`
            : lm.photo_url ? `📷 ${t("chat.composer.photo")}`
            : (lm.body || "…").slice(0, 60);
          toast(from, {
            description: bodyPrev,
            action: {
              label: t("fab.open_msg"),
              onClick: () => {
                setOpen(false);
                setMobileOverlay({ room: fresh, mode: "full" });
                markRead(r.id);
              },
            },
            duration: 5000,
          });
        }
        if (dingRoom) playDing();
      }
      unreadRef.current = Object.fromEntries(nextRooms.map((r) => [r.id, r.unread || 0]));
      firstLoad.current = false;
      setRooms(nextRooms);
      setOpenWindows((cur) => cur.map((w) => {
        const fresh = nextRooms.find((r) => r.id === w.id);
        return fresh ? { ...w, room: fresh } : w;
      }));
    } catch { /* ignore */ }
    setLoading(false);
  }, [user, authHeader, playDing, isDesktop, navigate]);

  // Keep a ref of open windows so `load()` can read the latest set
  // without re-creating itself (which would restart the polling loop).
  useEffect(() => { openWindowsRef.current = openWindows; }, [openWindows]);

  // Bridge SW-silenced chat pushes → in-app subtle ding. When the SW
  // decides not to show an OS notification (because a chat window is
  // already open), it broadcasts a message we consume here.
  useEffect(() => {
    if (typeof navigator === "undefined" || !navigator.serviceWorker) return;
    const onMsg = (evt) => {
      if (evt.data && evt.data.type === "DOSERNE_CHAT_PUSH") {
        playDing();
      }
    };
    navigator.serviceWorker.addEventListener("message", onMsg);
    return () => navigator.serviceWorker.removeEventListener("message", onMsg);
  }, [playDing]);

  // Global bridge: any page can open a DM by dispatching
  //   window.dispatchEvent(new CustomEvent("doserne:open-dm", {detail:{email}}))
  // Used by DoserneColleagues so the "Chat" button opens a floating
  // window on desktop instead of navigating away. Same pattern for
  // `doserne:open-project-chat` used by the projects list.
  useEffect(() => {
    if (!user) return undefined;
    const openRoomEverywhere = (room) => {
      load();
      if (!isDesktop) {
        // In-app overlay (no route change).
        setMobileOverlay({ room, mode: "full" });
        markRead(room.id);
      } else {
        setOpenWindows((cur) => {
          const existing = cur.find((w) => w.id === room.id);
          if (existing) {
            return [{ ...existing, room, minimized: false }, ...cur.filter((w) => w.id !== room.id)];
          }
          return [{ id: room.id, room, minimized: false }, ...cur].slice(0, maxWindowsRef.current);
        });
      }
    };
    const onOpenDm = async (evt) => {
      const email = evt?.detail?.email;
      if (!email) return;
      try {
        const { data } = await axios.post(
          `${API}/doserne/chat/rooms/dm`,
          { target_email: email },
          authHeader(),
        );
        openRoomEverywhere(data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || t("fab.dm_open_failed"));
      }
    };
    const onOpenProjectChat = async (evt) => {
      const project_id = evt?.detail?.project_id;
      if (!project_id) return;
      try {
        const { data } = await axios.post(
          `${API}/doserne/chat/rooms/project`,
          { project_id },
          authHeader(),
        );
        openRoomEverywhere(data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || t("fab.project_chat_failed"));
      }
    };
    window.addEventListener("doserne:open-dm", onOpenDm);
    window.addEventListener("doserne:open-project-chat", onOpenProjectChat);
    return () => {
      window.removeEventListener("doserne:open-dm", onOpenDm);
      window.removeEventListener("doserne:open-project-chat", onOpenProjectChat);
    };
  }, [user, authHeader, isDesktop, navigate, load]);

  const loadColleagues = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await axios.get(`${API}/doserne/colleagues`, authHeader());
      setColleagues((data.colleagues || []).filter((c) => !c.is_me));
    } catch { /* ignore */ }
  }, [user, authHeader]);

  useEffect(() => {
    if (!user) return;
    load();
    loadColleagues();
    const iv = setInterval(load, 15000);
    return () => clearInterval(iv);
  }, [user, load, loadColleagues]);

  const colleagueLabels = React.useMemo(() => {
    const map = {};
    for (const c of colleagues) map[c.email] = c.full_name || c.email;
    if (user) map[user.email] = user.full_name || user.email;
    return map;
  }, [colleagues, user]);

  const onChatPage = location.pathname.startsWith("/doserne/chat");

  // Prune windows when navigating to the chat page itself (avoid duplicate UI).
  useEffect(() => {
    if (onChatPage) setOpenWindows([]);
  }, [onChatPage]);

  // Bridge: SW-derived custom event "doserne:open-chat-sheet"
  // opens the target room as a 70dvh bottom-sheet overlay. This is
  // used when the user taps a chat push notification — we no longer
  // hard-navigate to /doserne/chat, we just slide up a Messenger-
  // style sheet on top of whatever page they were on. On desktop we
  // fall back to a floating window.
  useEffect(() => {
    const onOpenSheet = async (evt) => {
      const roomId = evt?.detail?.roomId;
      if (!roomId) return;
      try {
        // Room may or may not be in local state yet.
        let room = rooms.find((r) => r.id === roomId);
        if (!room) {
          const { data } = await axios.get(`${API}/doserne/chat/rooms`, authHeader());
          room = (data.rooms || []).find((r) => r.id === roomId);
        }
        if (!room) return;
        if (!isDesktop) {
          setMobileOverlay({ room, mode: "sheet" });
          markReadSafe(roomId);
        } else {
          setOpenWindows((cur) => {
            const existing = cur.find((w) => w.id === roomId);
            if (existing) return [{ ...existing, room, minimized: false }, ...cur.filter((w) => w.id !== roomId)];
            return [{ id: roomId, room, minimized: false }, ...cur].slice(0, maxWindowsRef.current);
          });
        }
      } catch { /* ignore */ }
    };
    // Small helper so this effect stays independent of the outer
    // `markRead` (which is declared after this hook).
    const markReadSafe = async (rid) => {
      try {
        await axios.post(`${API}/doserne/chat/rooms/${rid}/read`, {}, authHeader());
      } catch { /* ignore */ }
      setRooms((cur) => cur.map((x) => x.id === rid ? { ...x, unread: 0 } : x));
    };
    window.addEventListener("doserne:open-chat-sheet", onOpenSheet);
    // Also bridge from SW postMessage → same event.
    const onSwMsg = (evt) => {
      if (evt.data && evt.data.type === "DOSERNE_OPEN_CHAT_SHEET" && evt.data.roomId) {
        window.dispatchEvent(new CustomEvent("doserne:open-chat-sheet", { detail: { roomId: evt.data.roomId } }));
      }
    };
    if (typeof navigator !== "undefined" && navigator.serviceWorker) {
      navigator.serviceWorker.addEventListener("message", onSwMsg);
    }
    // On mount, check URL params for chatsheet=roomId (SW notification
    // click without an existing client uses this fallback path).
    try {
      const url = new URL(window.location.href);
      const rid = url.searchParams.get("chatsheet");
      if (rid) {
        url.searchParams.delete("chatsheet");
        window.history.replaceState({}, "", url.pathname + (url.searchParams.toString() ? `?${url.searchParams.toString()}` : "") + url.hash);
        setTimeout(() => window.dispatchEvent(new CustomEvent("doserne:open-chat-sheet", { detail: { roomId: rid } })), 300);
      }
    } catch { /* ignore */ }
    return () => {
      window.removeEventListener("doserne:open-chat-sheet", onOpenSheet);
      if (typeof navigator !== "undefined" && navigator.serviceWorker) {
        navigator.serviceWorker.removeEventListener("message", onSwMsg);
      }
    };
  }, [rooms, authHeader, isDesktop]);

  // Body scroll lock while the mobile overlay is open so iOS Safari
  // doesn't scroll the underlying page when the keyboard opens.
  // Skipped while minimized so the user can freely scroll the page
  // beneath the mini-pill.
  useEffect(() => {
    if (!mobileOverlay || mobileOverlay.minimized) return undefined;
    const origBody = document.body.style.overflow;
    const origHtml = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = origBody;
      document.documentElement.style.overflow = origHtml;
    };
  }, [mobileOverlay]);

  if (!user) return null;

  const markRead = async (roomId) => {
    try {
      await axios.post(`${API}/doserne/chat/rooms/${roomId}/read`, {}, authHeader());
    } catch { /* ignore */ }
    setRooms((cur) => cur.map((x) => x.id === roomId ? { ...x, unread: 0 } : x));
  };

  const openRoom = (r) => {
    setOpen(false);
    if (!isDesktop) {
      // Mobile: open an in-app overlay so the user stays on the
      // current page (e.g. Projects) instead of routing to
      // /doserne/chat. This restores multitasking — the FAB list and
      // its backdrop close automatically because of setOpen(false).
      setMobileOverlay({ room: r, mode: "full" });
      markRead(r.id);
      return;
    }
    // Desktop: open a floating window.
    setOpenWindows((cur) => {
      const existing = cur.find((w) => w.id === r.id);
      if (existing) {
        // Un-minimize + bring to front (index 0).
        return [{ ...existing, room: r, minimized: false }, ...cur.filter((w) => w.id !== r.id)];
      }
      const next = [{ id: r.id, room: r, minimized: false }, ...cur];
      return next.slice(0, maxWindowsRef.current);
    });
    markRead(r.id);
  };

  const closeWindow = (id) =>
    setOpenWindows((cur) => cur.filter((w) => w.id !== id));

  const minimizeWindow = (id) =>
    setOpenWindows((cur) => cur.map((w) => w.id === id ? { ...w, minimized: !w.minimized } : w));

  const expandWindow = (id) => {
    setOpenWindows((cur) => cur.filter((w) => w.id !== id));
    navigate(`/doserne/chat?room=${id}`);
  };

  const onMuteToggled = (rid, m) =>
    setRooms((cur) => cur.map((x) => x.id === rid ? { ...x, muted: m } : x));

  const hideFab = onChatPage;

  return (
    <>
      {/* Desktop floating windows */}
      {isDesktop && openWindows.map((w, idx) => (
        <ChatWindow
          key={w.id}
          index={idx}
          room={w.room}
          myEmail={user.email}
          authHeader={authHeader}
          colleagueLabels={colleagueLabels}
          minimized={w.minimized}
          onMinimize={() => minimizeWindow(w.id)}
          onClose={() => closeWindow(w.id)}
          onExpand={() => expandWindow(w.id)}
          onMuteToggled={onMuteToggled}
        />
      ))}

      {/* FAB button. On mobile it sits at bottom-24 so it doesn't
          collide with the DoserneShell floating Meny FAB (which lives
          at bottom-4). On desktop we go back to the tighter bottom-5.
          When a chat is minimized we route the click to re-open that
          chat (not the conversation drawer), and show a tiny avatar
          badge on the FAB so the user knows it's holding a chat. */}
      {!hideFab && (
        <button
          onClick={() => {
            // Minimized chat takes priority — restore it.
            // BUT if there are unread messages in OTHER rooms, open the
            // conversation drawer so the user sees the new incoming
            // message instead of getting silently re-thrown into the
            // parked chat.
            if (mobileOverlay?.minimized) {
              const currentId = mobileOverlay.room.id;
              const hasOtherUnread = rooms.some(
                (r) => r.id !== currentId && !r.muted && (r.unread || 0) > 0
              );
              if (hasOtherUnread) {
                setOpen((v) => !v);
                if (!open) load();
                return;
              }
              setMobileOverlay((o) => o ? { ...o, minimized: false } : o);
              return;
            }
            setOpen((v) => !v);
            if (!open) load();
          }}
          className="fixed z-40 bottom-24 sm:bottom-5 right-5 w-14 h-14 rounded-full bg-gradient-to-br from-[#0084ff] to-[#0064d1] hover:from-[#1094ff] hover:to-[#0074e1] shadow-xl flex items-center justify-center transition-transform active:scale-95"
          style={{ color: "#fff" }}
          data-testid="chat-fab"
          aria-label={t("fab.open_msg")}
        >
          {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
          {!open && totalUnread > 0 && (
            <span
              className="absolute -top-1 -right-1 min-w-[22px] h-[22px] px-1.5 rounded-full bg-red-500 border-2 border-white text-[11px] font-black flex items-center justify-center"
              style={{ color: "#fff" }}
              data-testid="chat-fab-badge"
            >
              {totalUnread > 99 ? "99+" : totalUnread}
            </span>
          )}
          {/* Mini avatar badge in the bottom-right corner of the FAB
              indicating there's a minimized chat parked inside. */}
          {mobileOverlay?.minimized && (() => {
            const r = mobileOverlay.room;
            const other = otherMember(r, user.email);
            const label = r.type === "dm"
              ? (colleagueLabels[other] || other)
              : (r.name || "");
            return (
              <span
                className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-white border-2 border-[#0064d1] flex items-center justify-center text-[10px] font-black text-slate-900 shadow"
                data-testid="chat-fab-minimized-badge"
                title={label}
              >
                {r.type === "group" || r.type === "project"
                  ? <Users className="w-3 h-3 text-slate-700" />
                  : (label || "?").slice(0, 1).toUpperCase()}
              </span>
            );
          })()}
        </button>
      )}

      {/* Conversations drawer (opens from FAB) */}
      {open && !hideFab && (
        <>
        {/* Transparent backdrop — click closes the drawer (Messenger UX). */}
        <div
          className="fixed inset-0 z-30"
          onClick={() => setOpen(false)}
          data-testid="chat-fab-drawer-backdrop"
        />
        <div
          className="fixed z-40 bottom-24 sm:bottom-24 right-5 w-[50vw] sm:w-[360px] h-[70dvh] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden"
          data-testid="chat-fab-drawer"
        >
          <div className="px-4 py-3 bg-gradient-to-r from-[#0084ff] to-[#0064d1] flex items-center justify-between" style={{ color: "#fff" }}>
            <div>
              <div className="text-[10px] uppercase tracking-widest opacity-90">Doserne</div>
              <div className="text-base font-black">{t("nav.chat")}</div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => { setShowSearch(true); setOpen(false); }}
                className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-white/20 hover:bg-white/30"
                style={{ color: "#fff" }}
                title={t("fab.search_convos")}
                data-testid="chat-fab-search"
              >
                <Search className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={toggleSound}
                className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-white/20 hover:bg-white/30"
                style={{ color: "#fff" }}
                title={soundOn ? t("fab.mute_all") : t("fab.unmute_all")}
                data-testid="chat-fab-sound-toggle"
              >
                {soundOn ? <Bell className="w-3.5 h-3.5" /> : <BellOff className="w-3.5 h-3.5" />}
              </button>
              <button
                onClick={() => { setShowNew(true); setOpen(false); }}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-white/20 hover:bg-white/30 text-xs font-black"
                style={{ color: "#fff" }}
                title={t("fab.new_conversation")}
                data-testid="chat-fab-new"
              >
                <Edit3 className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => { setOpen(false); navigate("/doserne/chat"); }}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-white/20 hover:bg-white/30 text-xs font-black"
                style={{ color: "#fff" }}
                title={t("fab.open_full")}
                data-testid="chat-fab-open-full"
              >
                <Maximize2 className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => setOpen(false)}
                className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-white/20 hover:bg-white/30"
                style={{ color: "#fff" }}
                title={t("fab.close") || "Lukk"}
                data-testid="chat-fab-close"
                aria-label="Lukk"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Inline search — filter colleagues + rooms without leaving
              the drawer. Uses local `drawerQuery` state; the more
              powerful message search still lives in ChatSearchModal. */}
          <div className="px-3 py-2 border-b border-slate-100 bg-white flex-shrink-0">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
              <input
                value={drawerQuery}
                onChange={(e) => setDrawerQuery(e.target.value)}
                placeholder={t("chat.newroom.search_colleague") || "Søk kolleger eller prosjekter…"}
                className="w-full pl-8 pr-8 py-1.5 text-xs bg-slate-100 border border-slate-200 rounded-lg focus:outline-none focus:border-[#0084ff] focus:bg-white"
                data-testid="chat-fab-inline-search"
              />
              {drawerQuery && (
                <button
                  onClick={() => setDrawerQuery("")}
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full hover:bg-slate-200 flex items-center justify-center"
                  aria-label="Clear"
                  data-testid="chat-fab-inline-search-clear"
                >
                  <X className="w-3 h-3 text-slate-500" />
                </button>
              )}
            </div>
          </div>

          {/* Colleagues strip — horizontal, online first, tap to open DM.
              Mirrors Messenger's "Active" story bar so users can start a
              chat with anyone without hunting through the room list.
              Hidden while the user is typing in the inline search
              since the filtered rooms list already surfaces matches. */}
          {colleagues.length > 0 && !drawerQuery.trim() && (
            <div className="border-b border-slate-100 bg-slate-50/60 px-2 py-2" data-testid="chat-fab-colleagues-strip">
              <div className="flex items-center justify-between px-1 mb-1.5">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-black">
                  {t("chat.header.colleagues") || "Kolleger"}
                </div>
                <div className="text-[10px] text-slate-400">
                  {colleagues.filter((c) => c.is_online).length} pålogget
                </div>
              </div>
              <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide" style={{ scrollbarWidth: "none" }}>
                {[...colleagues]
                  .sort((a, b) => {
                    if (!!b.is_online - !!a.is_online) return !!b.is_online - !!a.is_online;
                    return (a.full_name || a.email).localeCompare(b.full_name || b.email);
                  })
                  .map((c) => {
                    const label = c.full_name || c.email;
                    const initial = (label || "?").trim().charAt(0).toUpperCase();
                    return (
                      <button
                        key={c.email}
                        onClick={async () => {
                          try {
                            const { data } = await axios.post(
                              `${API}/doserne/chat/rooms/dm`,
                              { target_email: c.email },
                              authHeader(),
                            );
                            setRooms((cur) => {
                              const exists = cur.find((r) => r.id === data.id);
                              return exists ? cur.map((r) => r.id === data.id ? data : r) : [data, ...cur];
                            });
                            openRoom(data);
                          } catch (e) {
                            toast.error(e?.response?.data?.detail || "Kunne ikke åpne chat");
                          }
                        }}
                        className="flex flex-col items-center flex-shrink-0 w-14 group"
                        data-testid={`chat-fab-colleague-${c.email}`}
                        title={label}
                      >
                        <div className="relative">
                          <div className="w-11 h-11 rounded-full bg-gradient-to-br from-emerald-400 to-cyan-500 flex items-center justify-center text-white text-sm font-black group-hover:scale-105 transition">
                            {initial}
                          </div>
                          {c.is_online && (
                            <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-500 border-2 border-white" />
                          )}
                        </div>
                        <div className="mt-1 text-[10px] font-semibold text-slate-700 truncate w-full text-center leading-tight">
                          {label.split(" ")[0]}
                        </div>
                      </button>
                    );
                  })}
              </div>
            </div>
          )}

          <div className="flex-1 overflow-y-auto">
            {push.supported && !push.subscribed && push.permission !== "denied" && (
              <button
                onClick={async () => {
                  try {
                    await push.enable();
                    toast.success(t("fab.push_enabled"));
                  } catch (e) {
                    toast.error(e?.message || t("fab.push_failed"));
                  }
                }}
                disabled={push.busy}
                className="w-full text-left px-3 py-2 bg-amber-50 hover:bg-amber-100 border-b border-amber-200 flex items-center gap-2 disabled:opacity-50"
                data-testid="chat-fab-enable-push"
              >
                <Bell className="w-4 h-4 text-amber-700 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-black text-amber-900">{t("fab.enable_push")}</div>
                </div>
              </button>
            )}
            {loading && rooms.length === 0 ? (
              <div className="p-8 text-center text-xs text-slate-400">{t("common.loading")}</div>
            ) : rooms.length === 0 ? (
              <div className="p-8 text-center text-xs text-slate-400 italic">{t("fab.no_messages")}</div>
            ) : (() => {
              // Filter rooms by query — matches on label (DM = other's
              // name, group/project = room.name) OR last message body.
              const q = drawerQuery.trim().toLowerCase();
              const filtered = !q ? rooms : rooms.filter((r) => {
                const other = otherMember(r, user.email);
                const label = r.type === "dm" ? (colleagueLabels[other] || other) : (r.name || "");
                const bodyPreview = r.last_message?.body || "";
                return label.toLowerCase().includes(q) || bodyPreview.toLowerCase().includes(q);
              });

              // Also surface colleagues that don't yet have a DM room
              // when the user is searching (so they can start a chat
              // without going to the "Ny" modal). Skip when not
              // searching to preserve the classic recent-first flow.
              const roomOtherEmails = new Set(
                rooms.filter((r) => r.type === "dm").map((r) => otherMember(r, user.email).toLowerCase())
              );
              const matchingNewContacts = !q ? [] : colleagues.filter((c) => {
                if (roomOtherEmails.has(c.email.toLowerCase())) return false;
                const name = (c.full_name || c.email).toLowerCase();
                return name.includes(q) || c.email.toLowerCase().includes(q);
              });

              if (filtered.length === 0 && matchingNewContacts.length === 0) {
                return (
                  <div className="p-8 text-center text-xs text-slate-400 italic">
                    {q ? `Ingen treff for "${drawerQuery}"` : t("fab.no_messages")}
                  </div>
                );
              }

              return (
                <>
                  {filtered.map((r) => {
                    const other = otherMember(r, user.email);
                    const label = r.type === "dm" ? (colleagueLabels[other] || other) : (r.name || (r.type === "project" ? t("chat.header.project") : t("chat.header.group")));
                    const preview = r.last_message
                      ? (r.last_message.audio_url ? `🎙️ ${t("chat.composer.voice_note")}` : r.last_message.photo_url ? `📷 ${t("chat.composer.photo")}` : (r.last_message.body || ""))
                      : t("fab.no_messages");
                    return (
                      <button
                        key={r.id}
                        onClick={() => openRoom(r)}
                        className="w-full text-left px-3 py-2.5 border-b border-slate-100 hover:bg-slate-50 flex items-center gap-3"
                        data-testid={`chat-fab-room-${r.id}`}
                      >
                        <div className={`relative w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${r.type === "group" ? "bg-amber-100 text-amber-700" : r.type === "project" ? "bg-cyan-100 text-cyan-700" : "bg-slate-200 text-slate-600"}`}>
                          {r.type === "group" || r.type === "project" ? <Users className="w-4 h-4" /> : (label || "?").slice(0, 1).toUpperCase()}
                          {r.type === "dm" && isOnline(r.other_last_seen_at) && (
                            <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-emerald-500 border-2 border-white" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <div className="text-sm font-black text-slate-900 truncate flex items-center gap-1">
                              {label}
                              {r.muted && <BellOff className="w-3 h-3 text-slate-400 flex-shrink-0" />}
                            </div>
                            {r.last_message?.created_at && <div className="text-[10px] text-slate-400 flex-shrink-0">{timeAgo(r.last_message.created_at)}</div>}
                          </div>
                          <div className="flex items-center justify-between gap-2">
                            <div className="text-xs text-slate-500 truncate">{preview}</div>
                            {r.unread > 0 && !r.muted && (
                              <div className="text-[10px] bg-[#0084ff] rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1.5 font-black flex-shrink-0" style={{ color: "#fff" }}>
                                {r.unread}
                              </div>
                            )}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                  {matchingNewContacts.length > 0 && (
                    <>
                      <div className="px-3 py-1.5 bg-slate-50 border-b border-slate-100 text-[10px] uppercase tracking-widest text-slate-500 font-black">
                        Start ny samtale
                      </div>
                      {matchingNewContacts.map((c) => {
                        const label = c.full_name || c.email;
                        return (
                          <button
                            key={c.email}
                            onClick={async () => {
                              try {
                                const { data } = await axios.post(
                                  `${API}/doserne/chat/rooms/dm`,
                                  { target_email: c.email },
                                  authHeader(),
                                );
                                setRooms((cur) => {
                                  const exists = cur.find((r) => r.id === data.id);
                                  return exists ? cur.map((r) => r.id === data.id ? data : r) : [data, ...cur];
                                });
                                openRoom(data);
                              } catch (e) {
                                toast.error(e?.response?.data?.detail || "Kunne ikke åpne chat");
                              }
                            }}
                            className="w-full text-left px-3 py-2 border-b border-slate-100 hover:bg-emerald-50 flex items-center gap-3"
                            data-testid={`chat-fab-new-contact-${c.email}`}
                          >
                            <div className="relative w-9 h-9 rounded-full bg-gradient-to-br from-emerald-400 to-cyan-500 flex items-center justify-center text-white text-sm font-black flex-shrink-0">
                              {(label || "?").slice(0, 1).toUpperCase()}
                              {c.is_online && (
                                <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-emerald-500 border-2 border-white" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-black text-slate-900 truncate">{label}</div>
                              <div className="text-[11px] text-slate-500 truncate">{c.email}</div>
                            </div>
                            <Plus className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                          </button>
                        );
                      })}
                    </>
                  )}
                </>
              );
            })()}
          </div>
        </div>
        </>
      )}

      {showNew && (
        <NewRoomModal
          onClose={() => setShowNew(false)}
          authHeader={authHeader}
          colleagues={colleagues}
          myEmail={user.email}
          onCreated={(r) => {
            setRooms((cur) => [r, ...cur.filter((x) => x.id !== r.id)]);
            setShowNew(false);
            openRoom(r);
          }}
        />
      )}

      <ChatSearchModal
        open={showSearch}
        onClose={() => setShowSearch(false)}
        authHeader={authHeader}
        onOpenRoom={(roomId) => {
          const r = rooms.find((x) => x.id === roomId);
          if (r) openRoom(r);
          else if (!isDesktop) {
            // Fallback: open sheet using the event bridge.
            window.dispatchEvent(new CustomEvent("doserne:open-chat-sheet", { detail: { roomId } }));
          } else {
            navigate(`/doserne/chat?room=${roomId}`);
          }
        }}
      />

      {/* -----------------------------------------------------------
          MOBILE IN-APP OVERLAY (chat over current page, no route).
          - mode "full" is an 85dvh sheet anchored to the bottom with
            a rounded top (used when opening from the FAB list).
          - mode "sheet" is a shorter 70dvh bottom-sheet with a drag
            handle (used from push notification taps).
          Both close with a backdrop tap or the X button, and can be
          minimized to a small pill at the bottom-right that reopens
          the same room.
          ----------------------------------------------------------- */}
      {mobileOverlay && !isDesktop && !mobileOverlay.minimized && (
        <>
          <div
            className="fixed inset-0 z-40 bg-slate-900/40 backdrop-blur-[2px]"
            onClick={() => setMobileOverlay((o) => o ? { ...o, minimized: true } : o)}
            data-testid="chat-overlay-backdrop"
          />
          <div
            className={
              mobileOverlay.mode === "sheet"
                ? "fixed inset-x-0 bottom-0 z-50 h-[70dvh] bg-white rounded-t-2xl shadow-2xl border-t border-slate-200 flex flex-col overflow-hidden"
                : "fixed inset-x-0 bottom-0 z-50 h-[85dvh] bg-white rounded-t-2xl shadow-2xl border-t border-slate-200 flex flex-col overflow-hidden"
            }
            data-testid={`chat-overlay-${mobileOverlay.mode}`}
          >
            <div className="pt-2 pb-1 flex justify-center flex-shrink-0">
              <div className="w-10 h-1 rounded-full bg-slate-300" />
            </div>
            <div className="flex-1 min-h-0">
              <RoomView
                key={mobileOverlay.room.id}
                room={mobileOverlay.room}
                myEmail={user.email}
                authHeader={authHeader}
                colleagueLabels={colleagueLabels}
                compact
                onMuteToggled={onMuteToggled}
                otherRooms={rooms.filter((r) => r.id !== mobileOverlay.room.id)}
                onSwitchRoom={(r) => {
                  setMobileOverlay({ room: r, mode: mobileOverlay.mode, minimized: false });
                  markRead(r.id);
                }}
                headerRight={
                  <div className="flex items-center gap-0.5 ml-1">
                    <button
                      onClick={() => setMobileOverlay((o) => o ? { ...o, minimized: true } : o)}
                      className="w-7 h-7 rounded-full hover:bg-white/20 flex items-center justify-center"
                      style={{ color: "#fff" }}
                      title={t("fab.minimize")}
                      data-testid="chat-overlay-minimize"
                      aria-label="Minimer"
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => {
                        const rid = mobileOverlay.room.id;
                        setMobileOverlay(null);
                        navigate(`/doserne/chat?room=${rid}`);
                      }}
                      className="w-7 h-7 rounded-full hover:bg-white/20 flex items-center justify-center"
                      style={{ color: "#fff" }}
                      title={t("fab.open_full")}
                      data-testid="chat-overlay-expand"
                    >
                      <Maximize2 className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => setMobileOverlay(null)}
                      className="w-7 h-7 rounded-full hover:bg-white/20 flex items-center justify-center"
                      style={{ color: "#fff" }}
                      title={t("fab.close")}
                      data-testid="chat-overlay-close"
                      aria-label="Lukk"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                }
              />
            </div>
          </div>
        </>
      )}

      {/* When minimized, we no longer render a separate pill — the
          chat is "parked" inside the main chat FAB (see the mini
          avatar badge above). Tapping the FAB restores the overlay.
          This avoids the pill collision with the FAB icon. */}
    </>
  );
}
